import ImageTile from "ol/ImageTile";
import olOSMSource from "ol/source/OSM";
import olTileState from "ol/TileState";

interface RGB {
  red: number;
  green: number;
  blue: number;
}

interface HSV {
  hue: number;
  saturation: number;
  value: number;
}

/**
 * Service to create an OL OpenStreet Map tile source for use in Tile layers.
 * The tiles will be intercepted and converted to greyscale.
 * @returns {import("ol/source/OSM").default} an OSM source object
 */
const OSMGreyscaleDark = (attributions: string[]) => {
  const BlobToImageData = (blob: Blob) => {
    let blobUrl = URL.createObjectURL(blob);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = blobUrl;
    }).then((img: any) => {
      URL.revokeObjectURL(blobUrl);
      const canvas = document.createElement("canvas");
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      return ctx?.getImageData(0, 0, 256, 256);
    });
  };

  const ToGreyscale = (imageData: ImageData | undefined) => {
    if (!imageData) return;
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      var hsv = RGBtoHSV({
        red: data[i],
        green: data[i + 1],
        blue: data[i + 2],
      });

      hsv.saturation = 0;
      hsv.value = hsv.value < 128 ? hsv.value + 40 : hsv.value - 40;
      hsv.value = 255 - hsv.value;

      const rgb = HSVtoRGB(hsv);
      data[i + 0] = rgb.red;
      data[i + 1] = rgb.green;
      data[i + 2] = rgb.blue;
      data[i + 3] = 255; // Alpha
    }

    return imageData;
  };

  const RGBtoHSV = ({ red, green, blue }: RGB): HSV => {
    const r = red / 256;
    const g = green / 256;
    const b = blue / 256;
    const maxColor = Math.max(r, Math.max(g, b));
    const minColor = Math.min(r, Math.min(g, b));
    const v = maxColor;
    const s = maxColor === 0 ? 0 : (maxColor - minColor) / maxColor;
    let h = 0;
    if (s > 0) {
      if (r === maxColor) h = (g - b) / (maxColor - minColor);
      else if (g === maxColor) h = 2.0 + (b - r) / (maxColor - minColor);
      else h = 4.0 + (r - g) / (maxColor - minColor);
      h /= 6.0; //to bring it to a number between 0 and 1
      if (h < 0) h++;
    }

    return {
      hue: h * 255,
      saturation: s * 255,
      value: v * 255,
    };
  };

  const HSVtoRGB = ({ hue, saturation, value }: HSV): RGB => {
    let r = 0;
    let g = 0;
    let b = 0;
    let h = hue / 256;
    const s = saturation / 256;
    const v = value / 256;

    if (s === 0) r = g = b = v;
    else {
      h *= 6; //to bring hue to a number between 0 and 6, better for the calculations
      const i = Math.floor(h); //e.g. 2.7 becomes 2 and 3.01 becomes 3 or 4.9999 becomes 4
      const f = h - i; //the fractional part of h
      const p = v * (1 - s);
      const q = v * (1 - s * f);
      const t = v * (1 - s * (1 - f));
      switch (i) {
        case 0:
          r = v;
          g = t;
          b = p;
          break;
        case 1:
          r = q;
          g = v;
          b = p;
          break;
        case 2:
          r = p;
          g = v;
          b = t;
          break;
        case 3:
          r = p;
          g = q;
          b = v;
          break;
        case 4:
          r = t;
          g = p;
          b = v;
          break;
        case 5:
          r = v;
          g = p;
          b = q;
          break;
      }
    }

    return {
      red: r * 255,
      green: g * 255,
      blue: b * 255,
    };
  };

  const ImageDataToDataUrl = (imageData: ImageData | undefined) => {
    if (!imageData) return;
    const canvas = document.createElement("canvas");
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx?.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  };

  return new olOSMSource({
    attributions: attributions,
    tileLoadFunction: (tile, src) => {
      fetch(src)
        .then(async (response) => {
          if (!response.ok) {
            return Promise.reject(response.status);
          }
          return response.blob();
        })
        .then(async (data) => {
          return BlobToImageData(data);
        })
        .then((imageData) => {
          const greyscale = ToGreyscale(imageData);
          const dataUrl = ImageDataToDataUrl(greyscale);
          if (!dataUrl) return Promise.reject();
          const imageTile = tile as ImageTile;
          const image = imageTile.getImage() as HTMLImageElement;
          image.src = dataUrl;
        })
        .catch(() => {
          tile.setState(olTileState.ERROR);
        });
    },
  });
};

export default OSMGreyscaleDark;
